import { apiAnalytics, type IdentifyPayload } from '@api/analytics';
import { apiUser } from '@api/user';
import { getAuthApiErrorMessage } from '@helpers/auth-api-error';
import { pushDataLayerEvent } from '@helpers/dataLayer';
import { useFetch } from '@helpers/use-fetch';
import { FILE_TYPE } from '@models/quiz';
import {
  ResetPasswordPayload,
  SetPasswordPayload,
  SetPasswordResponse,
  UserLoginPayload,
  UserRegisterPayload,
  SessionInfoResponse,
  ProfileResponse,
  UserLoginResponse,
  UserLoginTOTPResponse,
} from '@models/user';
import { useAnalyticsStore } from '@stores/analytics';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useToast } from 'vue-toast-notification';

export const useUserStore = defineStore('user', () => {
  const toast = useToast();
  const info = ref<SessionInfoResponse>();
  const profileInfo = ref<ProfileResponse>();
  const loading = ref(false);
  const files = ref<{ [type: string]: string | null }>({});
  const analyticsStore = useAnalyticsStore();

  const getProfileApi = useFetch(apiUser.getProfile);
  const updateProfileApi = useFetch(apiUser.updateProfile);
  const removePaymentMethodApi = useFetch(apiUser.removePaymentMethod);
  const getPaymentMethodApi = useFetch(apiUser.getPaymentMethod);

  const uploadMethods = {
    [FILE_TYPE.DOCUMENT]: apiUser.uploadDocument,
    [FILE_TYPE.PHOTO]: apiUser.uploadPhoto,
    [FILE_TYPE.ID_PICTURE]: apiUser.uploadIDPicture,
    [FILE_TYPE.INSURANCE_FRONT]: apiUser.uploadInsuranceFront,
    [FILE_TYPE.INSURANCE_BACK]: apiUser.uploadInsuranceBack,
  };

  const deleteMethods = {
    [FILE_TYPE.DOCUMENT]: apiUser.deleteDocument,
    [FILE_TYPE.PHOTO]: apiUser.deletePhoto,
    [FILE_TYPE.ID_PICTURE]: apiUser.deleteIDPicture,
    [FILE_TYPE.INSURANCE_FRONT]: apiUser.deleteInsuranceFront,
    [FILE_TYPE.INSURANCE_BACK]: apiUser.deleteInsuranceBack,
  };

  const getInfo = async () => {
    try {
      loading.value = true;
      const response = await apiUser.sessionInfo();
      info.value = response.data;
      window.csrfTokenName = response.data.csrfTokenName;
      window.csrfTokenValue = response.data.csrfTokenValue;
      return response;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      toast.error(getAuthApiErrorMessage(message));
    } finally {
      loading.value = false;
    }
  };

  const refreshUserProfile = async () => {
    const profileResponse = await getProfileApi.execute({});
    if (profileResponse) {
      profileInfo.value = profileResponse.data;
    }
  };

  /**
   * Fire a RudderStack identify call with all available user traits.
   * Safe to call with only session info (profile not yet loaded) or with full profile data.
   * Optional `overrides` are used when profile data is not yet loaded (e.g. right after registration).
   *
   * Two paths fire simultaneously:
   *  1. Client-side SDK  → RudderStack data plane (immediate, handles anonymous→identified stitching)
   *  2. Server-side API  → Craft /api/v1/identify → SQS → Lambda → RudderStack HTTP API
   */
  const identifyUser = (overrides?: { firstName?: string; lastName?: string; email?: string }) => {
    if (!info.value || info.value.isGuest) return;

    const profile = profileInfo.value?.user;
    const addr = profile?.addresses.shippingAddress ?? profile?.addresses.billingAddress ?? null;

    const firstName = profile?.firstName ?? overrides?.firstName;
    const lastName = profile?.lastName ?? overrides?.lastName;
    const fullName = firstName || lastName ? `${firstName ?? ''} ${lastName ?? ''}`.trim() : undefined;

    const traits: IdentifyPayload = {
      email: profile?.email ?? overrides?.email ?? info.value.email,
      firstName,
      lastName,
      name: fullName,
      phone: profile?.phone || undefined,
      gender: profile?.gender || undefined,
      birthday: profile?.dob
        ? new Date(profile.dob * 1000).toISOString().split('T')[0]
        : undefined,
      username: info.value.username,
      ...(addr && {
        address: {
          street: addr.addressLine1 || undefined,
          city: addr.locality || undefined,
          state: addr.administrativeArea || undefined,
          postalCode: addr.postalCode || undefined,
          country: addr.countryCode || undefined,
        },
      }),
    };

    // Server-side: Craft → RudderStack SQS → Lambda → RudderStack HTTP API
    //    Server will enrich with its own user data resolution on top of these traits.
    apiAnalytics.identify(traits).catch(() => {
      // Non-blocking — if identify fails, session continues
    });
  };

  const register = async (data: UserRegisterPayload) => {
    try {
      if (analyticsStore.campaign) {
        data.campaign = analyticsStore.campaign;
      }
      if (analyticsStore.fbc) {
        data.fbc = analyticsStore.fbc;
      }
      if (analyticsStore.efTransactionId) {
        data.efTransactionId = analyticsStore.efTransactionId;
      }
      if (analyticsStore.utmSource) {
        data.utmSource = analyticsStore.utmSource;
      }
      if (analyticsStore.utmMedium) {
        data.utmMedium = analyticsStore.utmMedium;
      }

      loading.value = true;
      const response = await apiUser.register(data);

      const infoResponse = await getInfo();
      if (infoResponse) {
        analyticsStore.createAccount(infoResponse.data);
        identifyUser({ firstName: data.firstName, lastName: data.lastName, email: data.email });
      }

      pushDataLayerEvent('Account Created', data.email).catch(() => {});

      if (data.consent?.consentEmailGeneralMarketing || data.consent?.consentEmailPersonalizedQuiz) {
        pushDataLayerEvent('MarketingGenericConsent', data.email).catch(() => {});
      }

      return response;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      toast.error(getAuthApiErrorMessage(message));
    } finally {
      loading.value = false;
    }
  };

  const isUserTOTPResponse = (response: UserLoginResponse | UserLoginTOTPResponse) => {
    return 'authMethod' in response && response.authMethod === 'craft\\auth\\methods\\TOTP';
  };

  const login = async (data: UserLoginPayload) => {
    try {
      loading.value = true;
      const response = await apiUser.login(data);

      if (response && isUserTOTPResponse(response)) {
        return response;
      }

      await getInfo();

      // Refresh profile store and re-init analytics with new data
      try {
        await refreshUserProfile();
        identifyUser();
      } catch (e) {
        // Ignore errors
      }

      return response;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      toast.error(getAuthApiErrorMessage(message));
    } finally {
      loading.value = false;
    }
  };

  const verifyTotp = async (code: string) => {
    try {
      loading.value = true;
      const response = await apiUser.verifyTOTP({ code });

      await getInfo();

      // Refresh profile store and re-init analytics with new data
      try {
        await refreshUserProfile();
        identifyUser();
      } catch (e) {
        // Ignore errors
      }

      return response;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      toast.error(getAuthApiErrorMessage(message));
    } finally {
      loading.value = false;
    }
  };

  const resetPassword = async (data: ResetPasswordPayload) => {
    try {
      loading.value = true;
      const response = await apiUser.resetPassword(data);
      toast.success(response.message || 'Password reset email sent.');
      return response;
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : '';
      toast.error(getAuthApiErrorMessage(message));
    } finally {
      loading.value = false;
    }
  };

  const setPassword = async (
    data: SetPasswordPayload,
    options?: { quiet?: boolean },
  ) => {
    try {
      loading.value = true;
      const response = await apiUser.setPassword(data) as SetPasswordResponse & {
        csrfTokenValue?: string;
      };

      if (response?.csrfTokenValue) {
        window.csrfTokenValue = response.csrfTokenValue;
      }

      if (!options?.quiet) {
        toast.success(response.message || 'Password changed successfully.');
      }

      return response;
    } catch (e: unknown) {
      const message = getAuthApiErrorMessage(
        e instanceof Error ? e.message : undefined,
      );
      toast.error(message || 'Unable to reset password. Please request a new link and try again.');
      return {
        message: '',
        errors: [message],
      };
    } finally {
      loading.value = false;
    }
  };

  const uploadFile = async (type: FILE_TYPE, data: FormData) => {
    try {
      loading.value = true;
      const response = await uploadMethods[type](data);
      files.value[type] = response.data;

      return response;
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      loading.value = false;
    }
  };

  const deleteFile = async (type: FILE_TYPE) => {
    try {
      loading.value = true;
      const response = await deleteMethods[type]();
      delete files.value[type];

      return response;
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      loading.value = false;
    }
  };

  return {
    info,
    profileInfo,
    loading,
    getInfo,
    register,
    isUserTOTPResponse,
    login,
    verifyTotp,
    resetPassword,
    setPassword,
    files,
    uploadFile,
    deleteFile,
    getProfileApi,
    refreshUserProfile,
    updateProfileApi,
    removePaymentMethodApi,
    getPaymentMethodApi,
    identifyUser,
  };
});
